import { isPlatformBrowser, NgTemplateOutlet, NgClass, SlicePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter,
  HostBinding, input, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation
} from '@angular/core';

import { SdRatingComponent } from '@seb/web-design-system/reviews';
import { IDividedPayment } from '@seb/web-design-system/payment';
import { SdProductSellerComponent } from '@seb/web-design-system/product-seller';
import { SdProductBenefitComponent } from '@seb/web-design-system/product-benefit';
import { SdReparabilityComponent } from '@seb/web-design-system/reparability';

import {
  DeviceService, IBenefit, IconSizeEnum, IMedia, IPromo, IRating, IReparability, SdBannerComponent,
  SdIconComponent, SdLinkComponent, SdPriceComponent, SdProductPromoComponent,
  SdSkeletonComponent, SdThemeComponent, SdTitleComponent, SizesEnum,
  SdHtmlComponent, SkeletonAppearanceEnum, ThemesEnum
} from '@seb/web-design-system/core';

import { IKlarna } from './product-info.interface';
import { IStock } from '@seb/web-design-system/core/src/lib/_interfaces/stock.interface';

@Component({
  selector: 'sd-product-info',
  templateUrl: './product-info.component.html',
  styleUrls: ['./product-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    SdTitleComponent,
    SdLinkComponent,
    NgTemplateOutlet,
    SdRatingComponent,
    NgClass,
    SdIconComponent,
    SdProductSellerComponent,
    SdPriceComponent,
    SdProductPromoComponent,
    SdProductBenefitComponent,
    SdSkeletonComponent,
    SlicePipe,
    TranslateModule,
    SdBannerComponent,
    SdReparabilityComponent,
    SdHtmlComponent
  ]
})
export class SdProductInfoComponent extends SdThemeComponent implements OnInit, OnChanges {
  @Input()
  ratingSize: SizesEnum;

  @Input()
  showRef = false;

  @Input()
  reparabilityData: IReparability;

  @Input()
  indexFollow = true;

  @Input()
  isLoading = false;

  @Input()
  isProductDetails = false;

  @HostBinding('class.is-small')
  @Input()
  isSmall: boolean;

  @Input()
  isMinified: boolean;

  @Input()
  title: string;

  @Input()
  subtitle: string;

  @Input()
  productUrl: string;

  @Input()
  displayStock = true;

  @Input()
  dividedData: IDividedPayment;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel: number;

  @Input()
  largeTitle: boolean;

  @Input()
  benefits: IBenefit[];

  @Input()
  price: string;

  @Input()
  ecoTax: string;

  @Input()
  discountedPrice: string;

  @Input()
  stock: IStock;

  @Input()
  ratings: IRating;

  @Input()
  isTitleAriaHidden = false;

  @Input()
  klarnaData?: IKlarna;

  @Input()
  themesConfig: ThemesEnum.light | ThemesEnum.dark = ThemesEnum.dark;

  @Input()
  recommendedPrice: string;

  @Input()
  isSmallRecommendedPrice: boolean;

  @Input()
  isDiscountWithRecommendedPrice: boolean;

  @Input()
  productSeller: { text?: string; icon?: string };

  @Input()
  onSale: boolean;

  @Input()
  isTaxesIncluded = false;

  @Input()
  hideRatingStars: string;

  @Input()
  hideRatingLabel: string;

  @Input()
  hideRatingNumber: string;

  @Input()
  isBundle: boolean;

  @Input()
  isForfait: boolean;

  @Input()
  deliveryText: string;

  @Input()
  promoQualiRepar: IPromo;

  @Input()
  productRefSAV: string[];

  @Input()
  productCode: string;

  @Input()
  horizontalParentLayout = false;

  @Input()
  brandLogoTitle: IMedia;

  @Input()
  isSSR = true;
  iAdvizeId = input<string>();
  iAdvizeEnabled = input<boolean>(false);

  @Output()
  productClicked = new EventEmitter<void>();

  @Output()
  dividedClick: EventEmitter<void> = new EventEmitter();

  @Output()
  repairabilityInfoClick = new EventEmitter<void>();

  @Output()
  repairabilityLinkClick = new EventEmitter<void>();

  @Output()
  reviewsSeeAllClicked = new EventEmitter<void>();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  @Output()
  productSellerModal = new EventEmitter<boolean>();

  @Input()
  class = ''; // override the standard class attr with a new one.

  @Input()
  pageTemplate: string;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.themesConfig ? 'is-theme-' + this.themesConfig : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  skeletonAppearanceEnum = SkeletonAppearanceEnum;
  iconSize = IconSizeEnum;
  isMobile$: boolean;
  mobileSubscription: Subscription;
  numberOfRefSavToDisplay = 2;

  constructor(
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
  ) {
    super();
  }

  ngOnInit() {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        window['KlarnaOnsiteService'] ??= [];
        window['KlarnaOnsiteService'].push({ eventName: 'refresh-placements' });
      }, 100);
    }
  }

  onDividedClick() {
    this.dividedClick.emit();
  }

  onProductClick() {
    this.productClicked.emit();
  }

  onRepairabilityInfoClick() {
    this.repairabilityInfoClick.emit();
  }

  onRepairabilityLinkClick() {
    this.repairabilityLinkClick.emit();
  }

  onReviewsSeeAllClicked() {
    this.reviewsSeeAllClicked.emit();
  }

  onopenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }

  toggleProductSellerModal(event) {
    this.productSellerModal.emit(event);
  }

  onClickReadMore(url?: string) {
    if (url) {
      this.router.navigate([url], { queryParams: { openRef: true } });
    }
  }
}
